/**
 * Vendors Management Page.
 *
 * Full vendor CRUD with multi-contact management. Each vendor can have
 * multiple contacts with different roles (sales, accounting, shipping, etc.).
 * Designed to be embedded in the Procurement Hub.
 *
 * The vendor create/edit drawers are rendered at the page level (not inside
 * Card) to avoid CSS stacking-context issues caused by Card's fade-in
 * transform animation breaking fixed-position overlays.
 */

import { useState } from "react";
import {
  Plus,
  Trash2,
  Building2,
  UserPlus,
  Users,
  Phone,
  Mail,
  Globe,
  Star,
  X,
  Edit2,
  Power,
  PowerOff,
  MapPin,
  CreditCard,
  FileText,
} from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { toast } from "@/stores/toastStore";
import type { PaginatedResponse } from "@/types/common";
import type { Vendor, VendorContact } from "@/types/purchase-order";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import Input from "@/components/ui/Input";
import Card from "@/components/ui/Card";
import ActionDrawer from "@/components/ui/ActionDrawer";
import ConfirmDeleteDialog from "@/components/ui/ConfirmDeleteDialog";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

/* ========================================================================
   Types
   ======================================================================== */

interface VendorDraft {
  name: string;
  contactName: string;
  email: string;
  phone: string;
  website: string;
  taxId: string;
  taxTags: string;
  taxExempt: string;
  defaultTaxState: string;
  paymentTerms: string;
  currency: string;
  notes: string;
  street1: string;
  street2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

const emptyDraft: VendorDraft = {
  name: "",
  contactName: "",
  email: "",
  phone: "",
  website: "",
  taxId: "",
  taxTags: "",
  taxExempt: "false",
  defaultTaxState: "",
  paymentTerms: "",
  currency: "",
  notes: "",
  street1: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
};

function vendorToDraft(v: Vendor): VendorDraft {
  return {
    name: v.name,
    contactName: v.contact_name || "",
    email: v.email || "",
    phone: v.phone || "",
    website: v.website || "",
    taxId: v.tax_id || "",
    taxTags: (v.tax_tags || []).join(", "),
    taxExempt: v.tax_exempt ? "true" : "false",
    defaultTaxState: v.default_tax_state || "",
    paymentTerms: v.payment_terms || "",
    currency: v.default_currency || "",
    notes: v.notes || "",
    street1: v.address?.street1 || "",
    street2: v.address?.street2 || "",
    city: v.address?.city || "",
    state: v.address?.state || "",
    zip: v.address?.zip || "",
    country: v.address?.country || "",
  };
}

function draftToPayload(d: VendorDraft) {
  const hasAddress =
    d.street1 || d.street2 || d.city || d.state || d.zip || d.country;
  const tags = d.taxTags
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  return {
    name: d.name,
    contact_name: d.contactName || undefined,
    email: d.email || undefined,
    phone: d.phone || undefined,
    website: d.website || undefined,
    tax_id: d.taxId || undefined,
    tax_tags: tags.length > 0 ? tags : undefined,
    tax_exempt: d.taxExempt === "true" ? true : undefined,
    default_tax_state: d.defaultTaxState || undefined,
    payment_terms: d.paymentTerms || undefined,
    default_currency: d.currency || undefined,
    notes: d.notes || undefined,
    address: hasAddress
      ? {
          street1: d.street1 || undefined,
          street2: d.street2 || undefined,
          city: d.city || undefined,
          state: d.state || undefined,
          zip: d.zip || undefined,
          country: d.country || undefined,
        }
      : undefined,
  };
}

/* ========================================================================
   Main Page
   ======================================================================== */

function VendorsPage() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<PaginatedResponse<Vendor>>({
    queryKey: ["vendors"],
    queryFn: async () =>
      (await apiClient.get("/api/v1/vendors?page_size=100"))
        .data as PaginatedResponse<Vendor>,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);
  const [editingVendor, setEditingVendor] = useState<Vendor | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);

  const [draft, setDraft] = useState<VendorDraft>(emptyDraft);
  const updateField = (field: keyof VendorDraft, value: string) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const createMutation = useMutation({
    mutationFn: async () =>
      (await apiClient.post("/api/v1/vendors", draftToPayload(draft))).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      setShowCreate(false);
      setDraft(emptyDraft);
      toast.success("Vendor created", `${draft.name} has been added.`);
    },
    onError: () => toast.error("Failed to create vendor", "Please try again."),
  });

  const updateMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.put(
          `/api/v1/vendors/${editingVendor!.id}`,
          draftToPayload(draft),
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      setEditingVendor(null);
      setDraft(emptyDraft);
      toast.success("Vendor updated");
    },
    onError: () => toast.error("Failed to update vendor"),
  });

  const deactivateMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/v1/vendors/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      toast.success("Vendor deactivated");
    },
    onError: () => toast.error("Failed to deactivate vendor"),
  });

  const reactivateMutation = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.put(`/api/v1/vendors/${id}`, { is_active: true })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
      toast.success("Vendor reactivated");
    },
    onError: () => toast.error("Failed to reactivate vendor"),
  });

  const vendors = data?.items || [];

  const startCreate = () => {
    setDraft(emptyDraft);
    setEditingVendor(null);
    setShowCreate(true);
  };

  const startEdit = (vendor: Vendor) => {
    setDraft(vendorToDraft(vendor));
    setShowCreate(false);
    setEditingVendor(vendor);
  };

  const cancelEdit = () => {
    setEditingVendor(null);
    setDraft(emptyDraft);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">
            Vendors
          </h2>
          <p className="text-sm text-[var(--text-secondary)] mt-1">
            Manage your supply chain vendors and their contacts.
          </p>
        </div>
        <Button size="sm" onClick={startCreate}>
          <Plus className="h-4 w-4" /> Add Vendor
        </Button>
      </div>

      {/* Create Vendor Drawer — page level */}
      <ActionDrawer
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Vendor"
        description="Add a new vendor to your supply chain."
        icon={Building2}
        size="xl"
      >
        <VendorFormSections
          draft={draft}
          updateField={updateField}
          onSubmit={() => createMutation.mutate()}
          isPending={createMutation.isPending}
          submitLabel="Create Vendor"
        />
      </ActionDrawer>

      {/* Edit Vendor Drawer — page level, outside Card transform context */}
      <ActionDrawer
        isOpen={!!editingVendor}
        onClose={cancelEdit}
        title={editingVendor ? `Edit ${editingVendor.name}` : "Edit Vendor"}
        description="Update vendor details."
        icon={Edit2}
        size="xl"
      >
        <VendorFormSections
          draft={draft}
          updateField={updateField}
          onSubmit={() => updateMutation.mutate()}
          isPending={updateMutation.isPending}
          submitLabel="Save Changes"
        />
      </ActionDrawer>

      {/* Vendor List */}
      {isLoading ? (
        <LoadingSpinner className="py-12" />
      ) : vendors.length > 0 ? (
        <div className="space-y-3 stagger-children">
          {vendors.map((vendor) => {
            const isExpanded = expandedVendor === vendor.id;

            return (
              <div key={vendor.id}>
                <Card
                  padding="none"
                  className={!vendor.is_active ? "opacity-60" : ""}
                >
                  {/* Vendor row */}
                  <div className="flex items-center gap-3 px-4 py-3">
                    {/* Icon */}
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700 dark:bg-accent-900/30 dark:text-accent-400">
                      <Building2 className="h-5 w-5" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[var(--text-primary)]">
                          {vendor.name}
                        </span>
                        <Badge
                          variant={vendor.is_active ? "success" : "default"}
                        >
                          {vendor.is_active ? "Active" : "Inactive"}
                        </Badge>
                        {vendor.payment_terms && (
                          <Badge variant="info">{vendor.payment_terms}</Badge>
                        )}
                        {vendor.default_currency &&
                          vendor.default_currency !== "USD" && (
                            <Badge variant="warning">
                              {vendor.default_currency}
                            </Badge>
                          )}
                      </div>
                      <div className="mt-0.5 flex items-center gap-3 text-xs text-[var(--text-tertiary)] flex-wrap">
                        {vendor.contact_name && (
                          <span>{vendor.contact_name}</span>
                        )}
                        {vendor.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {vendor.email}
                          </span>
                        )}
                        {vendor.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {vendor.phone}
                          </span>
                        )}
                        {vendor.website && (
                          <span className="flex items-center gap-1">
                            <Globe className="h-3 w-3" />
                            {vendor.website}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="shrink-0 flex items-center gap-1.5">
                      <button
                        onClick={() =>
                          setExpandedVendor(isExpanded ? null : vendor.id)
                        }
                        className={`flex items-center gap-1.5 rounded-[var(--radius-lg)] border px-3 py-1.5 text-xs font-medium transition-all ${
                          isExpanded
                            ? "border-brand-300 bg-brand-100 text-brand-700 dark:border-brand-700 dark:bg-brand-900/20 dark:text-brand-300"
                            : "border-[var(--color-border)] text-[var(--text-secondary)] hover:border-brand-300 hover:text-brand-600"
                        }`}
                      >
                        <Users className="h-3.5 w-3.5" />
                        Contacts
                      </button>

                      <button
                        onClick={() => startEdit(vendor)}
                        title="Edit vendor"
                        className="flex items-center justify-center h-8 w-8 rounded-[var(--radius-lg)] border border-[var(--color-border)] text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:border-[var(--text-tertiary)] transition-colors"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>

                      {vendor.is_active ? (
                        <button
                          onClick={() => deactivateMutation.mutate(vendor.id)}
                          title="Deactivate vendor"
                          className="flex items-center justify-center h-8 w-8 rounded-[var(--radius-lg)] border border-[var(--color-border)] text-[var(--text-tertiary)] transition-colors hover:border-[var(--text-danger)]/30 hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)] dark:hover:border-red-800 dark:hover:bg-red-900/10"
                        >
                          <PowerOff className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          onClick={() => reactivateMutation.mutate(vendor.id)}
                          title="Reactivate vendor"
                          className="flex items-center justify-center h-8 w-8 rounded-[var(--radius-lg)] border border-[var(--color-border)] text-[var(--text-tertiary)] transition-colors hover:border-[var(--text-success)]/30 hover:text-[var(--text-success)] hover:bg-[var(--fill-success)] dark:hover:border-emerald-800 dark:hover:bg-emerald-900/10"
                        >
                          <Power className="h-3.5 w-3.5" />
                        </button>
                      )}

                      <button
                        onClick={() => setDeleteTarget(vendor.id)}
                        title="Permanently delete vendor"
                        className="flex items-center justify-center h-8 w-8 rounded-[var(--radius-lg)] border border-[var(--color-border)] text-[var(--text-tertiary)] transition-colors hover:border-[var(--text-danger)]/30 hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)]"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>

                  {/* Expanded: Contacts */}
                  {isExpanded && (
                    <div className="border-t border-[var(--color-border)] px-4 py-3 bg-[var(--color-surface-secondary)]/50">
                      <VendorContacts vendorId={vendor.id} />
                    </div>
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      ) : (
        <Card>
          <div className="flex flex-col items-center py-12">
            <div className="empty-state-icon">
              <Building2 />
            </div>
            <h3 className="mt-4 text-lg font-medium text-[var(--text-primary)]">
              No vendors
            </h3>
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              Add your first vendor to start tracking suppliers.
            </p>
          </div>
        </Card>
      )}

      <ConfirmDeleteDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        apiPath="/api/v1/vendors"
        entityId={deleteTarget || ""}
        queryKey="vendors"
      />
    </div>
  );
}

/* ========================================================================
   Vendor Form — Sectioned Layout
   ======================================================================== */

function SectionHeader({
  icon: Icon,
  title,
}: {
  icon: React.ElementType;
  title: string;
}) {
  return (
    <div className="flex items-center gap-2 pb-2 mb-3 border-b border-[var(--border-subtle)]">
      <Icon className="h-4 w-4 text-[var(--text-tertiary)]" />
      <span className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
        {title}
      </span>
    </div>
  );
}

function VendorFormSections({
  draft,
  updateField,
  onSubmit,
  isPending,
  submitLabel,
}: {
  draft: VendorDraft;
  updateField: (field: keyof VendorDraft, value: string) => void;
  onSubmit: () => void;
  isPending: boolean;
  submitLabel: string;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="space-y-6"
    >
      {/* Section: Company Information */}
      <div>
        <SectionHeader icon={Building2} title="Company Information" />
        <div className="space-y-3">
          <Input
            label="Company Name"
            value={draft.name}
            onChange={(e) => updateField("name", e.target.value)}
            required
            placeholder="Acme Equipment Co."
          />
          <Input
            label="Primary Contact Name"
            value={draft.contactName}
            onChange={(e) => updateField("contactName", e.target.value)}
            placeholder="John Smith"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Email"
              type="email"
              value={draft.email}
              onChange={(e) => updateField("email", e.target.value)}
              placeholder="sales@acme.com"
            />
            <Input
              label="Phone"
              value={draft.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              placeholder="555-123-4567"
            />
          </div>
          <Input
            label="Website"
            value={draft.website}
            onChange={(e) => updateField("website", e.target.value)}
            placeholder="https://acme.com"
          />
        </div>
      </div>

      {/* Section: Address */}
      <div>
        <SectionHeader icon={MapPin} title="Address" />
        <div className="space-y-3">
          <Input
            label="Street Address"
            value={draft.street1}
            onChange={(e) => updateField("street1", e.target.value)}
            placeholder="123 Industrial Blvd"
          />
          <Input
            label="Suite / Unit"
            value={draft.street2}
            onChange={(e) => updateField("street2", e.target.value)}
            placeholder="Suite 400"
          />
          <div className="grid grid-cols-[1fr_80px_100px] gap-3">
            <Input
              label="City"
              value={draft.city}
              onChange={(e) => updateField("city", e.target.value)}
              placeholder="Dallas"
            />
            <Input
              label="State"
              value={draft.state}
              onChange={(e) => updateField("state", e.target.value)}
              placeholder="TX"
            />
            <Input
              label="ZIP"
              value={draft.zip}
              onChange={(e) => updateField("zip", e.target.value)}
              placeholder="75201"
            />
          </div>
          <Input
            label="Country"
            value={draft.country}
            onChange={(e) => updateField("country", e.target.value)}
            placeholder="United States"
          />
        </div>
      </div>

      {/* Section: Financial & Terms */}
      <div>
        <SectionHeader icon={CreditCard} title="Financial & Terms" />
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
                Payment Terms
              </label>
              <select
                value={draft.paymentTerms}
                onChange={(e) => updateField("paymentTerms", e.target.value)}
                className="block w-full h-[38px] rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 text-sm text-[var(--text-primary)]"
              >
                <option value="">Select...</option>
                <option value="COD">COD</option>
                <option value="Due on Receipt">Due on Receipt</option>
                <option value="Net 15">Net 15</option>
                <option value="Net 30">Net 30</option>
                <option value="Net 45">Net 45</option>
                <option value="Net 60">Net 60</option>
                <option value="Prepaid">Prepaid</option>
              </select>
            </div>
            <Input
              label="Default Currency"
              value={draft.currency}
              onChange={(e) => updateField("currency", e.target.value)}
              placeholder="USD"
            />
          </div>
          <Input
            label="Tax ID / EIN"
            value={draft.taxId}
            onChange={(e) => updateField("taxId", e.target.value)}
            placeholder="12-3456789"
          />
          <div>
            <Input
              label="Tax Tags"
              value={draft.taxTags}
              onChange={(e) => updateField("taxTags", e.target.value)}
              placeholder="heavy-equipment, agriculture"
            />
            <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
              Comma-separated tags for tax scheme matching
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Input
                label="Default Tax State"
                value={draft.defaultTaxState}
                onChange={(e) =>
                  updateField("defaultTaxState", e.target.value.toUpperCase())
                }
                placeholder="TX"
                maxLength={2}
              />
              <p className="text-[11px] text-[var(--text-tertiary)] mt-1">
                2-letter state code for interstate tax
              </p>
            </div>
            <div>
              <label className="block text-[13px] font-medium text-[var(--text-primary)] mb-1.5">
                Tax Exempt
              </label>
              <label className="flex items-center gap-2 h-[38px] cursor-pointer">
                <input
                  type="checkbox"
                  checked={draft.taxExempt === "true"}
                  onChange={(e) =>
                    updateField(
                      "taxExempt",
                      e.target.checked ? "true" : "false",
                    )
                  }
                  className="rounded border-[var(--border-default)]"
                />
                <span className="text-sm text-[var(--text-secondary)]">
                  Exempt from purchase tax
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Section: Notes */}
      <div>
        <SectionHeader icon={FileText} title="Notes" />
        <div>
          <textarea
            value={draft.notes}
            onChange={(e) => updateField("notes", e.target.value)}
            placeholder="Additional notes about this vendor..."
            rows={3}
            className="block w-full rounded-[var(--radius-md)] border-[0.5px] border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] resize-none focus:outline-none focus:ring-2 focus:ring-[var(--color-interactive-primary)]/20 focus:border-[var(--color-interactive-primary)]"
          />
        </div>
      </div>

      {/* Submit */}
      <div className="flex justify-end pt-2 border-t border-[var(--border-subtle)]">
        <Button type="submit" isLoading={isPending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

/* ========================================================================
   Vendor Contacts Section
   ======================================================================== */

function VendorContacts({ vendorId }: { vendorId: string }) {
  const qc = useQueryClient();

  const { data: vendor, isLoading } = useQuery<Vendor>({
    queryKey: ["vendor", vendorId],
    queryFn: async () =>
      (await apiClient.get(`/api/v1/vendors/${vendorId}`)).data as Vendor,
  });

  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContactId, setEditingContactId] = useState<string | null>(null);

  const [cName, setCName] = useState("");
  const [cTitle, setCTitle] = useState("");
  const [cEmail, setCEmail] = useState("");
  const [cPhone, setCPhone] = useState("");
  const [cMobile, setCMobile] = useState("");
  const [cPrimary, setCPrimary] = useState(false);
  const [cNotes, setCNotes] = useState("");

  const resetContactForm = () => {
    setCName("");
    setCTitle("");
    setCEmail("");
    setCPhone("");
    setCMobile("");
    setCPrimary(false);
    setCNotes("");
  };

  const startEditContact = (c: VendorContact) => {
    setEditingContactId(c.id);
    setShowAddContact(false);
    setCName(c.name);
    setCTitle(c.title || "");
    setCEmail(c.email || "");
    setCPhone(c.phone || "");
    setCMobile(c.mobile || "");
    setCPrimary(c.is_primary);
    setCNotes(c.notes || "");
  };

  const cancelContactEdit = () => {
    setEditingContactId(null);
    resetContactForm();
  };

  const addContactMutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post(`/api/v1/vendors/${vendorId}/contacts`, {
          name: cName,
          title: cTitle || undefined,
          email: cEmail || undefined,
          phone: cPhone || undefined,
          mobile: cMobile || undefined,
          is_primary: cPrimary,
          notes: cNotes || undefined,
        })
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor", vendorId] });
      setShowAddContact(false);
      resetContactForm();
      toast.success("Contact added", `${cName} has been added.`);
    },
    onError: () => toast.error("Failed to add contact"),
  });

  const updateContactMutation = useMutation({
    mutationFn: async (contactId: string) =>
      (
        await apiClient.put(
          `/api/v1/vendors/${vendorId}/contacts/${contactId}`,
          {
            name: cName,
            title: cTitle || undefined,
            email: cEmail || undefined,
            phone: cPhone || undefined,
            mobile: cMobile || undefined,
            is_primary: cPrimary,
            notes: cNotes || undefined,
          },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor", vendorId] });
      cancelContactEdit();
      toast.success("Contact updated");
    },
    onError: () => toast.error("Failed to update contact"),
  });

  const deleteContactMutation = useMutation({
    mutationFn: async (contactId: string) => {
      await apiClient.delete(
        `/api/v1/vendors/${vendorId}/contacts/${contactId}`,
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor", vendorId] });
      toast.success("Contact deleted");
    },
    onError: () => toast.error("Failed to delete contact"),
  });

  const setPrimaryMutation = useMutation({
    mutationFn: async (contactId: string) =>
      (
        await apiClient.put(
          `/api/v1/vendors/${vendorId}/contacts/${contactId}`,
          { is_primary: true },
        )
      ).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendor", vendorId] });
      toast.success("Primary contact updated");
    },
    onError: () => toast.error("Failed to set primary contact"),
  });

  if (isLoading || !vendor) return <LoadingSpinner className="py-4" />;

  const contacts = vendor.contacts || [];

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-tertiary)]">
          Contacts ({contacts.length})
        </h4>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setShowAddContact(!showAddContact);
            cancelContactEdit();
          }}
        >
          {showAddContact ? (
            <>
              <X className="h-3 w-3" /> Cancel
            </>
          ) : (
            <>
              <UserPlus className="h-3 w-3" /> Add Contact
            </>
          )}
        </Button>
      </div>

      {/* Add Contact Form */}
      {showAddContact && (
        <ContactForm
          onSubmit={() => addContactMutation.mutate()}
          isPending={addContactMutation.isPending}
          submitLabel="Add Contact"
          name={cName}
          setName={setCName}
          title={cTitle}
          setTitle={setCTitle}
          email={cEmail}
          setEmail={setCEmail}
          phone={cPhone}
          setPhone={setCPhone}
          mobile={cMobile}
          setMobile={setCMobile}
          isPrimary={cPrimary}
          setIsPrimary={setCPrimary}
          notes={cNotes}
          setNotes={setCNotes}
        />
      )}

      {/* Contact cards */}
      {contacts.length > 0 ? (
        <div className="space-y-1.5">
          {contacts.map((contact) => {
            const isEditingThis = editingContactId === contact.id;

            return (
              <Card key={contact.id} padding="sm" className="hover-lift">
                {isEditingThis ? (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-[var(--text-secondary)]">
                        Editing Contact
                      </span>
                      <button
                        onClick={cancelContactEdit}
                        className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <ContactForm
                      onSubmit={() => updateContactMutation.mutate(contact.id)}
                      isPending={updateContactMutation.isPending}
                      submitLabel="Save Contact"
                      name={cName}
                      setName={setCName}
                      title={cTitle}
                      setTitle={setCTitle}
                      email={cEmail}
                      setEmail={setCEmail}
                      phone={cPhone}
                      setPhone={setCPhone}
                      mobile={cMobile}
                      setMobile={setCMobile}
                      isPrimary={cPrimary}
                      setIsPrimary={setCPrimary}
                      notes={cNotes}
                      setNotes={setCNotes}
                    />
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    {/* Avatar */}
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                        contact.is_primary
                          ? "bg-brand-200 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300"
                          : "bg-[var(--surface-sunken)] text-[var(--text-secondary)] dark:bg-gray-800 dark:text-[var(--text-tertiary)]"
                      }`}
                    >
                      {contact.name
                        .split(" ")
                        .map((n: string) => n[0])
                        .join("")
                        .toUpperCase()
                        .slice(0, 2)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-[var(--text-primary)]">
                          {contact.name}
                        </span>
                        {contact.is_primary && (
                          <span className="flex items-center gap-0.5 text-[10px] font-semibold text-brand-600">
                            <Star className="h-3 w-3 fill-brand-600" /> Primary
                          </span>
                        )}
                        {contact.title && (
                          <span className="text-xs text-[var(--text-tertiary)]">
                            · {contact.title}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-[var(--text-tertiary)] flex-wrap">
                        {contact.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {contact.email}
                          </span>
                        )}
                        {contact.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.phone}
                          </span>
                        )}
                        {contact.mobile && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {contact.mobile}
                          </span>
                        )}
                        {contact.notes && (
                          <span className="text-[var(--text-tertiary)] italic truncate max-w-[200px]">
                            {contact.notes}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="shrink-0 flex items-center gap-1">
                      {!contact.is_primary && (
                        <button
                          onClick={() => setPrimaryMutation.mutate(contact.id)}
                          title="Set as primary contact"
                          className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-md)] text-[var(--text-tertiary)] transition-colors hover:text-brand-600 hover:bg-brand-100 dark:hover:bg-brand-900/10"
                        >
                          <Star className="h-3.5 w-3.5" />
                        </button>
                      )}
                      <button
                        onClick={() => startEditContact(contact)}
                        title="Edit contact"
                        className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-md)] text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-primary)] hover:bg-[var(--color-surface-hover)]"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => deleteContactMutation.mutate(contact.id)}
                        title="Delete contact"
                        className="flex items-center justify-center h-7 w-7 rounded-[var(--radius-md)] text-[var(--text-tertiary)] transition-colors hover:text-[var(--text-danger)] hover:bg-[var(--fill-danger)] dark:hover:bg-red-900/10"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      ) : (
        <p className="text-center text-xs text-[var(--text-tertiary)] py-3">
          No contacts yet — add one above.
        </p>
      )}
    </div>
  );
}

/* ========================================================================
   Contact Form (reused for add + edit)
   ======================================================================== */

function ContactForm({
  onSubmit,
  isPending,
  submitLabel,
  name,
  setName,
  title,
  setTitle,
  email,
  setEmail,
  phone,
  setPhone,
  mobile,
  setMobile,
  isPrimary,
  setIsPrimary,
  notes,
  setNotes,
}: {
  onSubmit: () => void;
  isPending: boolean;
  submitLabel: string;
  name: string;
  setName: (v: string) => void;
  title: string;
  setTitle: (v: string) => void;
  email: string;
  setEmail: (v: string) => void;
  phone: string;
  setPhone: (v: string) => void;
  mobile: string;
  setMobile: (v: string) => void;
  isPrimary: boolean;
  setIsPrimary: (v: boolean) => void;
  notes: string;
  setNotes: (v: string) => void;
}) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className="grid grid-cols-1 gap-3 sm:grid-cols-3"
    >
      <Input
        label="Full Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        required
        placeholder="Jane Smith"
      />
      <Input
        label="Title / Role"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Sales Manager"
      />
      <Input
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="jane@vendor.com"
      />
      <Input
        label="Phone"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        placeholder="555-123-4567"
      />
      <Input
        label="Mobile"
        value={mobile}
        onChange={(e) => setMobile(e.target.value)}
        placeholder="555-987-6543"
      />
      <div className="flex items-end gap-2">
        <label className="flex items-center gap-2 text-sm text-[var(--text-secondary)] cursor-pointer">
          <input
            type="checkbox"
            checked={isPrimary}
            onChange={(e) => setIsPrimary(e.target.checked)}
            className="rounded border-[var(--color-border)]"
          />
          Primary contact
        </label>
      </div>
      <div className="sm:col-span-3">
        <Input
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Optional notes about this contact"
        />
      </div>
      <div className="sm:col-span-3 flex justify-end">
        <Button type="submit" size="sm" isLoading={isPending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

export default VendorsPage;
